import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Plus, Trash2, Send } from "lucide-react";

const Chat = ({
  conversations,
  currentConversation,
  messages,
  inputMessage,
  isLoading,
  messagesEndRef,
  setCurrentConversation,
  setInputMessage,
  createNewConversation,
  deleteConversation,
  sendMessage
}) => {
  return (
    <div className="flex h-full">
      {/* Lista de conversas */}
      <div className="w-80 border-r border-gray-800 flex flex-col bg-gray-900/50">
        <div className="p-4 border-b border-gray-800">
          <Button
            onClick={createNewConversation}
            className="w-full bg-orange-500 hover:bg-orange-600"
            data-testid="new-conversation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Conversa
          </Button>
        </div>

        <ScrollArea className="flex-1 p-2">
          {conversations.map((conv) => (
            <Card
              key={conv.id}
              className={`mb-2 p-3 cursor-pointer transition-colors group ${
                currentConversation?.id === conv.id
                  ? "bg-gray-800 border-orange-500"
                  : "bg-gray-800/50 border-gray-700 hover:bg-gray-800"
              }`}
              onClick={() => setCurrentConversation(conv)}
              data-testid={`conversation-${conv.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{conv.title}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(conv.updated_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conv.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-conversation-${conv.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </Card>
          ))}
        </ScrollArea>
      </div>

      {/* Área de chat */}
      <div className="flex-1 flex flex-col">
        <div className="h-16 border-b border-gray-800 flex items-center px-6 bg-gray-900/50">
          <h2 className="text-lg font-semibold">
            {currentConversation ? currentConversation.title : "Selecione uma conversa"}
          </h2>
        </div>

        <ScrollArea className="flex-1 p-6" data-testid="messages-area">
          {messages.length === 0 && !currentConversation && (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-semibold mb-2">Chat com Agente de IA</h3>
                <p className="text-gray-400">Comece uma nova conversa para interagir</p>
              </div>
            </div>
          )}

          {messages.length === 0 && currentConversation && (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-orange-500" />
                <h3 className="text-xl font-semibold mb-2">Conversa iniciada</h3>
                <p className="text-gray-400">Digite sua primeira mensagem abaixo</p>
              </div>
            </div>
          )}

          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
                data-testid={`message-${message.role}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-4 ${
                    message.role === "user"
                      ? "bg-orange-500 text-white"
                      : "bg-gray-800 text-gray-100"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs mt-2 opacity-70">
                    {new Date(message.timestamp).toLocaleTimeString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start" data-testid="loading-indicator">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-gray-800 p-4 bg-gray-900/50">
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 bg-gray-800 border-gray-700 text-white"
                disabled={!currentConversation || isLoading}
                data-testid="message-input"
              />
              <Button
                type="submit"
                disabled={!currentConversation || isLoading || !inputMessage.trim()}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;